/**
 * The Apps tab's in-page tabs of one service (components/apps/ServiceTabs): each project-wide answer narrowed to the
 * service. Data: the stores it uses (DataService.getOverview); Traces: who calls it and whom it calls (TracesService
 * getMap); Config: the names of its variables, secrets and jobs, never a value (AppConfigService.getServiceConfig);
 * Code: its files counted from a vfs tree; Logs: the last lines of the runner's stream.
 */
// The folders the sync never reads (utils/vfsClient.js SYNC_EXCLUDED_DIR_NAMES, vfs-service's heavy folders): kept here
// so this file stays free of the client.
const SKIPPED_DIRS = new Set([
  'node_modules', 'dist', 'build', 'out', '.next', '.nuxt', '.svelte-kit', '.output', '.cache', '.parcel-cache', '.turbo',
  '.vercel', '.netlify', 'coverage', 'target', '__pycache__', '.venv', 'venv', '.git',
]);

/** Whether two service names are the same service (`services/app` and `app`). */
export const sameService = (a, b) => String(a || '').replace(/^services\//, '') === String(b || '').replace(/^services\//, '');

export const MAX_OBJECTS = 8;

/** The stores the service uses, each with its biggest objects first: `[{ ...store, objects, more }]`. */
export function storesOf(stores, service) {
  return (stores || [])
    .filter((store) => (store.services || []).some((name) => sameService(name, service)))
    .map((store) => {
      const objects = [...(store.objects || [])].sort((a, b) => (b.count ?? -1) - (a.count ?? -1));
      return { ...store, objects: objects.slice(0, MAX_OBJECTS), more: Math.max(objects.length - MAX_OBJECTS, 0) };
    });
}

/**
 * The map's arrows into and out of the service's box, most calls first: `{ incoming: [{ id, peer, kind, calls, errors,
 * operations }], outgoing: [...] }`, or null when the map has no box for it (no request reached it in the range).
 */
export function traceLinks(map, service) {
  const nodes = map?.nodes || [];
  const node = nodes.find((item) => item.kind === 'service' && sameService(item.service || item.label, service));
  if (!node) { return null; }
  const byId = new Map(nodes.map((item) => [item.id, item]));
  const row = (edge, peerId) => ({
    id: edge.id,
    peer: byId.get(peerId)?.label || peerId,
    kind: byId.get(peerId)?.kind || 'node',
    calls: edge.calls || 0,
    errors: edge.errors || 0,
    operations: (edge.operations || []).filter((op) => op?.name).map((op) => op.name),
  });
  const sorted = (list) => list.sort((a, b) => b.calls - a.calls);
  const edges = map?.edges || [];
  return {
    node,
    incoming: sorted(edges.filter((edge) => edge.to === node.id).map((edge) => row(edge, edge.from))),
    outgoing: sorted(edges.filter((edge) => edge.from === node.id).map((edge) => row(edge, edge.to))),
  };
}

/** The names in a service's configuration, without any value: `{ vars: [name], secrets: [{ name, set }], jobs: [...] }`. */
export function configNames(detail) {
  return {
    vars: (detail?.vars || []).map((item) => item.name).filter(Boolean),
    // A file secret also says so, with its file's name and the second variable its path is in (never a content).
    secrets: (detail?.secrets || []).filter((item) => item?.name).map((item) => ({
      name: item.name,
      set: item.set !== false,
      ...(item.kind === 'file' ? { file: item.fileName || true, envName: item.envName || null } : {}),
    })),
    jobs: (detail?.jobs || []).filter((item) => item?.name).map((item) => ({
      name: item.name, when: item.describe || item.schedule || '', enabled: item.enabled !== false, lastStatus: item.lastRun?.status || null,
    })),
  };
}

export const MAX_TYPES = 5;
const typeOf = (name) => {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : name;
};

/**
 * Counts a service's files from its vfs tree (dependency and build folders left out, as the sync does): `{ files,
 * bytes, types: [{ type, count, bytes }], entry }`. `entry` is the file "Open Code" opens (a manifest or main file,
 * else the first), relative to the service folder.
 */
export function codeStats(tree) {
  const types = new Map();
  const paths = [];
  let bytes = 0;
  const walk = (node, prefix) => {
    for (const child of node?.children || []) {
      const path = prefix ? `${prefix}/${child.name}` : child.name;
      if (child.type === 'directory') {
        if (!SKIPPED_DIRS.has(child.name)) { walk(child, path); }
        continue;
      }
      paths.push(path);
      const size = Number(child.size) || 0;
      bytes += size;
      const type = typeOf(child.name);
      const seen = types.get(type) || { type, count: 0, bytes: 0 };
      types.set(type, { type, count: seen.count + 1, bytes: seen.bytes + size });
    }
  };
  walk(tree, '');
  const top = [...types.values()].sort((a, b) => b.count - a.count || b.bytes - a.bytes).slice(0, MAX_TYPES);
  return { files: paths.length, bytes, types: top, entry: entryFile(paths) };
}

const ENTRY_FILES = ['package.json', 'main.py', 'app.py', 'pyproject.toml', 'requirements.txt', 'main.go', 'go.mod', 'Cargo.toml', 'pom.xml', 'index.html', 'Dockerfile', 'docker-compose.yml', 'compose.yaml', 'README.md'];

/** The file to open a service's code on: a top-level manifest or main file, else the first file; null without files. */
export function entryFile(paths) {
  return ENTRY_FILES.find((name) => (paths || []).includes(name)) || (paths || [])[0] || null;
}

export const LOG_LINES = 100;

/**
 * The lines of the stream's first event (`initial`: what the runner kept), the running output when there is some,
 * else the build's, at most `max`, newest last: `[{ type, line }]`.
 */
export function logTail(initial, max = LOG_LINES) {
  const text = (line) => (typeof line === 'string' ? line : line?.message || line?.line || '');
  const timed = Array.isArray(initial?.timed) ? initial.timed.map((item) => ({ type: item.type, line: text(item.line) })) : null;
  const all = timed?.length ? timed : [
    ...(initial?.build || []).map((line) => ({ type: 'build', line: text(line) })),
    ...(initial?.stdout || []).map((line) => ({ type: 'stdout', line: text(line) })),
    ...(initial?.stderr || []).map((line) => ({ type: 'stderr', line: text(line) })),
  ];
  const running = all.filter((item) => item.type !== 'build');
  return (running.length ? running : all).slice(-max);
}
